/**
 * 用户管理 API 服务
 */
import { api, API_PATHS, PaginatedData } from './client';
import type { UserRole } from '@/types';

// 用户信息
export interface UserData {
  id: string;
  orgId: string;
  email: string;
  name: string;
  role: UserRole;
  groups: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// 列表查询参数
export interface ListUsersParams {
  page?: number;
  pageSize?: number;
  role?: string;
  group?: string;
  search?: string;
  [key: string]: string | number | boolean | undefined;
}

// 创建用户请求
export interface CreateUserRequest {
  email: string;
  name: string;
  password: string;
  role: UserRole;
  groups?: string[];
  isActive?: boolean;
}

// 更新用户请求
export interface UpdateUserRequest {
  email?: string;
  name?: string;
  password?: string;
  role?: UserRole;
  groups?: string[];
  isActive?: boolean;
}

// 批量删除请求
export interface BatchDeleteRequest {
  ids: string[];
}

// 导入结果
export interface ImportResult {
  total: number;
  success: number;
  failed: number;
  errors: Array<{
    row: number;
    email: string;
    error: string;
  }>;
}

/**
 * 获取用户列表
 */
export async function getUsers(params?: ListUsersParams): Promise<PaginatedData<UserData>> {
  return api.get<PaginatedData<UserData>>(API_PATHS.USERS, params);
}

/**
 * 创建用户
 */
export async function createUser(data: CreateUserRequest): Promise<UserData> {
  return api.post<UserData>(API_PATHS.USERS, data);
}

/**
 * 更新用户
 */
export async function updateUser(id: string, data: UpdateUserRequest): Promise<void> {
  await api.put<null>(`${API_PATHS.USERS}/${id}`, data);
}

/**
 * 删除用户
 */
export async function deleteUser(id: string): Promise<void> {
  await api.delete<null>(`${API_PATHS.USERS}/${id}`);
}

/**
 * 批量删除用户
 */
export async function batchDeleteUsers(ids: string[]): Promise<void> {
  await api.post<null>(API_PATHS.USERS_BATCH_DELETE, { ids });
}

/**
 * 导入用户（CSV）
 */
export async function importUsers(file: File): Promise<ImportResult> {
  const formData = new FormData();
  formData.append('file', file);

  const token = localStorage.getItem('myorg_token');
  const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8082'}${API_PATHS.USERS_IMPORT}`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: formData,
  });

  const result = await response.json();

  if (result.code !== 200) {
    throw new Error(result.message);
  }

  return result.data;
}

/**
 * 导出用户（CSV）
 */
export function exportUsersUrl(): string {
  const token = localStorage.getItem('myorg_token');
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8082';
  return `${baseUrl}${API_PATHS.USERS_EXPORT}?token=${token}`;
}
